import { type FC, useEffect } from 'react';
import { lazy } from 'react';
import { useMemoOne } from 'use-memo-one';
import type { CapacityGraphRequestParams } from 'src/apis/direct-fcfs/types';
import { useDirectFcfsCapacityGraph } from 'src/apis/direct-fcfs/use-direct-fcfs-capacity-graph';
import { useDirectFcfsCapacityPools } from 'src/apis/direct-fcfs/use-direct-fcfs-capacity-pools';
import type { NetworkPoint } from 'src/apis/monolith/types';
import type { NetworkPointRequest } from 'src/apis/monolith/use-network-point';
import { PageSpinner } from 'src/components/spinner-container';
import { useCapacityOverviewPageParams } from 'src/pages/transport/network-points/details/capacity-overview-fcfs/use-page-params';

const Page = lazy(() => import('./page'));

type ContentProps = {
  networkPoint: NetworkPoint;
};

const Content: FC<ContentProps> = ({ networkPoint }) => {
  const capacityPools = useDirectFcfsCapacityPools(
    networkPoint.networkPointUuid
  );
  const pageParams = useCapacityOverviewPageParams(capacityPools);
  useEffect(() => {
    if (!capacityPools?.response) return;
    pageParams.set({
      ...pageParams,
      capacityPoolId: capacityPools.response.data[0].id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacityPools?.response]);

  const paramsForGraph = useMemoOne<CapacityGraphRequestParams>(() => {
    return {
      validFrom: pageParams.value.runtimeIntersectionFilterStart,
      validTo: pageParams.value.runtimeIntersectionFilterEnd,
      capacityPoolId: pageParams.value.capacityPoolId,
    };
  }, [pageParams.value, capacityPools?.response]);

  const nwpCapacityGraph = useDirectFcfsCapacityGraph(
    networkPoint.networkPointUuid,
    paramsForGraph
  );
  if (!capacityPools?.response || !nwpCapacityGraph?.response)
    return <PageSpinner />;

  return (
    <Page
      networkPoint={networkPoint}
      pageParams={pageParams}
      capacityPools={capacityPools}
      nwpCapacityGraph={nwpCapacityGraph}
    />
  );
};

type Props = {
  networkPoint: NetworkPointRequest;
};

export const CapacityOverviewFcfs: FC<Props> = ({ networkPoint }) => {
  if (!networkPoint.response) return <PageSpinner />;

  return <Content networkPoint={networkPoint.response.data} />;
};
